import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate } from 'react-router-dom';
import AnalysisPage from './AnalysisPage';
import TestRigPage from './TestRigPage';
import HistoryPage from './HistoryPage';
import AnnotationPage from './AnnotationPage';
import AnalyzeWebsitePage from './AnalyzeWebsitePage';
import UsersAdminPage from './UsersAdminPage';
import ProfilePage from './ProfilePage';
import LoginGate from './LoginGate';
import { RuntimeSettingsProvider } from './context/RuntimeSettingsContext';
import { AuthProvider, useAuth, hasRole } from './context/AuthContext';

// Role-based route guard. Renders children only if `role` is allowed.
// `superuser` is implicitly allowed everywhere.
const RequireRole = ({ allowed, children }) => {
  const { role } = useAuth();
  if (!hasRole(role, ...allowed)) {
    // Redirect to a page they CAN see.
    if (role === 'annotator') return <Navigate to="/annotation" replace />;
    return <Navigate to="/" replace />;
  }
  return children;
};

const NavBar = () => {
  const { role, user, logout } = useAuth();
  const isAnnotator = role === 'annotator';
  const isUserOrUp = hasRole(role, 'user');
  const isSuper = role === 'superuser';

  return (
    <nav className="bg-white shadow-sm mb-4 sm:mb-8">
      <div className="max-w-5xl mx-auto px-3 sm:px-4 py-3 sm:py-4 flex justify-between items-center">
        <Link to={isAnnotator ? '/annotation' : '/'} className="font-bold text-lg sm:text-xl text-blue-600">
          Auction.ai
        </Link>
        <div className="flex items-center space-x-3 sm:space-x-5">
          {isUserOrUp && (
            <Link to="/history" className="text-sm sm:text-base text-slate-600 hover:text-blue-600">History</Link>
          )}
          {isSuper && (
            <Link to="/test-rig" className="text-sm sm:text-base text-slate-600 hover:text-blue-600">Test Rig</Link>
          )}
          {(isAnnotator || isSuper) && (
            <Link to="/annotation" className="text-sm sm:text-base text-slate-600 hover:text-blue-600">Annotation tool</Link>
          )}
          {isUserOrUp && (
            <Link to="/analyze-website" className="text-sm sm:text-base text-slate-600 hover:text-blue-600">Analyse web-site</Link>
          )}
          {isSuper && (
            <Link to="/users" className="text-sm sm:text-base text-slate-600 hover:text-blue-600">Users</Link>
          )}
          <Link to="/profile" className="text-sm sm:text-base text-slate-600 hover:text-blue-600">Profile</Link>
          <span className="text-xs text-slate-500 hidden sm:inline">
            {user?.username} <span className="px-1.5 py-0.5 ml-1 rounded bg-slate-100 text-slate-600">{role}</span>
          </span>
          <button onClick={logout} className="text-sm text-slate-500 hover:text-red-600">Log out</button>
        </div>
      </div>
    </nav>
  );
};

const AppShell = () => {
  const { role } = useAuth();
  const annotatorOnly = role === 'annotator';

  return (
    <Router>
      <div className="min-h-screen bg-slate-100">
        <NavBar />
        <main className="container mx-auto px-2 sm:px-4 pb-8 sm:pb-12">
          <Routes>
            {/* Annotator's "home" is the annotation tool. */}
            <Route
              path="/"
              element={
                annotatorOnly
                  ? <Navigate to="/annotation" replace />
                  : <RequireRole allowed={['user']}><AnalysisPage /></RequireRole>
              }
            />
            <Route
              path="/history"
              element={<RequireRole allowed={['user']}><HistoryPage /></RequireRole>}
            />
            <Route
              path="/analyze-website"
              element={<RequireRole allowed={['user']}><AnalyzeWebsitePage /></RequireRole>}
            />
            <Route
              path="/test-rig"
              element={<RequireRole allowed={[]}><TestRigPage /></RequireRole>}
            />
            <Route
              path="/annotation"
              element={<RequireRole allowed={['annotator']}><AnnotationPage /></RequireRole>}
            />
            <Route
              path="/users"
              element={<RequireRole allowed={[]}><UsersAdminPage /></RequireRole>}
            />
            <Route
              path="/profile"
              element={<RequireRole allowed={['user', 'annotator']}><ProfilePage /></RequireRole>}
            />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
    </Router>
  );
};

function App() {
  return (
    <AuthProvider>
      <LoginGate>
        <RuntimeSettingsProvider>
          <AppShell />
        </RuntimeSettingsProvider>
      </LoginGate>
    </AuthProvider>
  );
}

export default App;
